import { cn } from "@/lib/utils";
import { useId } from "react";
import "./CompanyLogoMarquee.css";

type CompanyLogo = {
  src: string;
  alt: string;
  /** Per-logo sizing — the artwork has wildly different aspect ratios. */
  size: string;
};

const COMPANY_LOGOS: CompanyLogo[] = [
  { src: "/vercel-logo.svg", alt: "Vercel", size: "h-8 max-w-[100px]" },
  { src: "/LaunchDarkly-Logo.png", alt: "LaunchDarkly", size: "h-10 max-w-[120px]" },
  { src: "/fivetran-logo-blue-rgb.jpg", alt: "Fivetran", size: "h-10 max-w-[100px]" },
  { src: "/gitlab.png", alt: "GitLab", size: "h-12 max-w-[100px]" },
  { src: "/calendly.webp", alt: "Calendly", size: "h-8 max-w-[100px]" },
  { src: "/Amazon_logo.svg", alt: "Amazon", size: "h-10 max-w-[100px]" },
  { src: "/nike.png", alt: "Nike", size: "h-12 max-w-[100px]" },
  { src: "/netflix.png", alt: "Netflix", size: "h-12 max-w-[100px]" },
  { src: "/dell.svg", alt: "Dell", size: "h-10 max-w-[100px]" },
  { src: "/Salesforce.com_logo.png", alt: "Salesforce", size: "h-12 max-w-[100px]" },
  { src: "/redhat.webp", alt: "Red Hat", size: "h-12 max-w-[100px]" },
  { src: "/Peloton-logo.jpg", alt: "Peloton", size: "h-10 max-w-[100px]" },
  { src: "/stryker-logo.svg", alt: "Stryker", size: "h-10 max-w-[100px]" },
  { src: "/worldline-logo.avif", alt: "Worldline", size: "h-10 max-w-[100px]" },
  { src: "/LegitScript_Logo.png", alt: "LegitScript", size: "h-10 max-w-[110px]" },
  { src: "/cashapp.svg", alt: "Cash App", size: "h-16 max-w-[100px]" },
];

// Each position cycles through its own logos instead of moving the whole row.
const LOGO_SLOTS = Array.from({ length: 6 }, (_, slot) =>
  COMPANY_LOGOS.filter((_, index) => index % 6 === slot),
);

interface CompanyLogoMarqueeProps {
  /** Eyebrow above the logos. */
  heading?: string;
  /** Optional line under the logos. */
  footnote?: string;
  /** Extra classes for the <section> wrapper (background, spacing, z-index). */
  className?: string;
}

const CompanyLogoMarquee = ({
  heading = "Training Leaders & Staff at Companies Like",
  footnote,
  className,
}: CompanyLogoMarqueeProps) => {
  const headingId = useId();

  return (
    <section
      className={cn("company-logo-scroller bg-white py-12", className)}
      aria-labelledby={headingId}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p id={headingId} className="text-center text-sm font-semibold uppercase tracking-wider text-gray-500 mb-10">
          {heading}
        </p>
        <ul className="sr-only">
          {COMPANY_LOGOS.map((logo) => <li key={logo.src}>{logo.alt}</li>)}
        </ul>
        <div className="company-logo-grid" aria-hidden="true">
          {LOGO_SLOTS.map((logos, slot) => (
            <div
              key={slot}
              className="company-logo-slot grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-[filter,opacity]"
              data-logo-count={logos.length}
            >
              {logos.map((logo) => (
                <img
                  key={logo.src}
                  src={logo.src}
                  alt=""
                  draggable={false}
                  decoding="async"
                  className={cn(
                    logo.size,
                    "company-logo-image w-auto object-contain",
                  )}
                />
              ))}
            </div>
          ))}
        </div>
        {footnote && <p className="mt-4 text-center text-sm text-gray-500">{footnote}</p>}
      </div>
    </section>
  );
};

export default CompanyLogoMarquee;
